import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectBuildingHeights } from '../../../store/selectors/databaseSelectors';
import { addBuildingHeight } from '../../../store/slices/databaseSlice';
import { buildingHeightsService } from '../../../services';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import toast from 'react-hot-toast';

interface BuildingHeightForm {
  value: string;
}

export const BuildingHeightManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get data from Redux
  const buildingHeights = useAppSelector(selectBuildingHeights);
  
  // Form states
  const [buildingHeightForm, setBuildingHeightForm] = useState<BuildingHeightForm>({
    value: ''
  });
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localBuildingHeights, setLocalBuildingHeights] = useState(buildingHeights);

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalBuildingHeights(buildingHeights);
  }, [buildingHeights]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!buildingHeightForm.value.trim()) {
      toast('Please enter a building height value', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newBuildingHeight = await buildingHeightsService.createBuildingHeight({
        value: buildingHeightForm.value.trim()
      });
      
      // Update Redux state
      dispatch(addBuildingHeight(newBuildingHeight));
      
      // Update local state - prepend new item
      setLocalBuildingHeights(prev => [newBuildingHeight, ...prev]);
      
      // Reset form
      setBuildingHeightForm({ value: '' });
      
      toast.success('Building height created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating building height:', error);
      toast.error('Failed to create building height. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            Add New Building Height
          </h3>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Height Classification *
              </label>
              <input
                type="text"
                value={buildingHeightForm.value}
                onChange={(e) => setBuildingHeightForm(prev => ({ ...prev, value: e.target.value }))}
                placeholder="e.g., Single Story, Two Story, Three+ Story"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !buildingHeightForm.value.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : 'Create Building Height'}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              Existing Building Heights
            </h3>
            <span className="text-xs text-slate-500">
              {localBuildingHeights.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {localBuildingHeights.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {localBuildingHeights.map((height) => (
                  <div 
                    key={height.id} 
                    className="inline-flex items-center px-3 py-1.5 rounded-full border bg-purple-50 text-purple-800 border-purple-200 hover:shadow-sm transition-shadow"
                  >
                    <span className="text-sm font-medium">{height.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-slate-400 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="text-slate-500 text-sm">No building heights configured yet</p>
                <p className="text-slate-400 text-xs mt-1">Add your first height classification using the form</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};